import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import PageContent from "./pages/PageContent";
import RegistrationFlow from "./pages/RegistrationFlow";
import StudentLogin from "./pages/StudentLogin";

// CHANGE 1: removed `import Contact from './pages/Contact';`
import StudentRegistrationNew from "./pages/StudentRegistrationNew";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout, { AdminPlaceholderRoute } from "./pages/admin/AdminLayout";
import AdminDashboardHome from "./pages/admin/AdminDashboardHome";
import ManageLinks from "./pages/admin/ManageLinks";
import ManageTenders from "./pages/admin/ManageTenders";
import ManageStudentRegistration from "./pages/admin/ManageStudentRegistration";
import ManageDownloadForms from "./pages/admin/ManageDownloadForms";
import ManageResults from "./pages/admin/ManageResults";
import ManageAdmission from "./pages/admin/ManageAdmission";
import ManageAdmissionCourse from "./pages/admin/ManageAdmissionCourse";
import ManageGovtApproval from "./pages/admin/ManageGovtApproval";
import ManageEvents from "./pages/admin/ManageEvents";   // ← ADD THIS
import ManageMainMenu from "./pages/admin/ManageMainMenu";
import AddMainMenu from "./pages/admin/AddMainMenu";
import EditMainMenu from "./pages/admin/EditMainMenu";
import ManageSubMenu from "./pages/admin/ManageSubMenu";
import ManageSubMenuForm from "./pages/admin/ManageSubMenuForm";
import ManageSubSubMenu from "./pages/admin/ManageSubSubMenu";
import AddSubSubMenu from "./pages/admin/AddSubSubMenu";
import EditSubSubMenu from "./pages/admin/EditSubSubMenu";
import ManageContents from "./pages/admin/ManageContents";
import AdminForgotPassword from "./pages/admin/AdminForgotPassword";


import ManageDownloadNotices from "./pages/admin/ManageDownloadnotices";
import ManageAdvertisement from "./pages/admin/ManageAdvertisement"; 
import ManageSuccess from "./pages/admin/ManageSuccess"; // ← NEW (type = 8)
import ManageCourse from "./pages/admin/ManageCourse"; 
import ManageDepartment from "./pages/admin/ManageDepartment";
import ManageMciInformation from "./pages/admin/ManageMciInformation"; 
import ManageActivityReport from "./pages/admin/ManageActivityReport"; 
import ManageSetting from "./pages/admin/ManageSetting"; 
import ChangePassword from "./pages/admin/ChangePassword";
import ManageSeo from "./pages/admin/ManageSeo";
import ManageBanner from "./pages/admin/ManageBanner"; 
import ManageDepartmentMaster from "./pages/admin/ManageDepartmentMaster";
import ManageDesignation from "./pages/admin/ManageDesignation";
import ManageFaculty from "./pages/admin/ManageFaculty"; 
import ManageGalleryCategory from "./pages/admin/ManageGalleryCategory";
import ManagePhotoGallery from "./pages/admin/ManagePhotoGallery";


import ForgotPassword from "./pages/ForgotPassword"; // adjust path to wherever you saved it
import ResetPassword from "./pages/ResetPassword";
import NoticeListPage from "./pages/NoticeListPage";
import WebLinkPage from "./pages/WebLinkPage";
// import GalleryPage from "./pages/GalleryPage";
// import GalleryCategoryPage from "./pages/GalleryCategoryPage";
import GalleryPage from "./pages/GalleryPage";
import ContactPage from "./pages/ContactPage"; 
import SiteMapPage from "./pages/SiteMapPage";
import FacultyPage from "./pages/FacultyPage";
import AdmissionsPage from "./pages/AdmissionsPage";
import GovtApprovalPage from "./pages/GovtApprovalPage";

import adminApi from "./pages/admin/adminApi";

import "./App.css";

// ── Activity logging ──────────────────────────────────────────────────────
// Ports title.php's behavior: that file was included on every admin page
// and inserted one row into tbl_activityreport per page load. Here, since
// AdminProtectedRoute stays mounted across admin route changes (React
// Router keeps the parent layout route mounted and only swaps the nested
// <Outlet /> content), a useLocation()-driven effect placed here fires
// once per admin page the logged-in user visits — same effect, SPA-style.

// Per-browser-tab id, generated once and reused for the life of the tab.
// Closest available substitute for PHP's session_id() — JWT auth here is
// stateless, so there's no real server-side session to log instead.
function getTabSessionId() {
  const KEY = "mkcg_admin_activity_session";
  let id = sessionStorage.getItem(KEY);
  if (!id) {
    id =
      (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
      Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(KEY, id);
  }
  return id;
}

function useActivityLogger() {
  const location = useLocation();

  useEffect(() => {
    const url = location.pathname + (location.search || "");
    const sessionId = getTabSessionId();

    adminApi
      .post("/admin/activity-report/log", { url, sessionId })
      .catch((err) => {
        // Logging must never block or break the admin page itself.
        console.error("Activity log failed:", err);
      });
   
  }, [location.pathname, location.search]);
}

// Guards every /admin/* page except the login itself — bounces back to /admin if there's no token
function AdminProtectedRoute({ children }) {
  const token = localStorage.getItem("mkcg_admin_token");

  // Only log once we know there IS a valid token — an unauthenticated
  // visitor bouncing straight back to /admin shouldn't generate a log row.
  useActivityLogger();

  return token ? children : <Navigate to="/admin" replace />;
}

// Layout wrapper with Navbar + Footer (for regular pages)
const MainLayout = ({ children }) => (
  <div className="app">
    <Navbar />
    {children}
    <Footer />
  </div>
);

export default function App() {
  return (
    <Router>
      <ScrollToTop />
      <Routes>
        {/* Registration gets full-screen layout (no Navbar/Footer) */}
        <Route path="/StudentLogin" element={<StudentLogin />} />
        <Route path="/StudentRegistration" element={<RegistrationFlow />} />
        <Route path="/student-registration-new" element={<StudentRegistrationNew />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* CHANGE 3: removed the old standalone
            <Route path="/contact" element={<Contact />} />
            from here — it's now nested inside MainLayout below,
            so it renders with the Navbar (menu header) + Footer. */}

        {/* Admin/staff portal — login + protected pages now live under ONE shared
            "/admin" parent route instead of two separate top-level routes
            ("/admin" and "/admin/*"). That old sibling-route setup was the actual
            cause of the blank page: React Router's ranking algorithm gives an
            index route extra weight, so the "/admin/*" branch's index redirect
            was winning the match for the exact "/admin" URL instead of the plain
            "/admin" login route — which sent it straight into
            AdminProtectedRoute, which (no token yet) immediately redirected back
            to "/admin", which matched the same branch again, looping silently
            with nothing ever painted to the screen. Nesting everything under one
            parent removes that ambiguity entirely: "/admin" can now only ever
            resolve to the login page below. */}
        <Route path="/admin">
          {/* /admin exact — full-screen login, no public Navbar/Footer */}
          <Route index element={<AdminLogin />} />
          <Route path="forgot-password" element={<AdminForgotPassword />} />

          {/* /admin/dashboard, /admin/links, /admin/tenders, etc. — real per-page
              routes under a shared sidebar+header layout, gated by
              AdminProtectedRoute. Each sidebar item has its own URL, so refresh,
              back/forward, and bookmarking all still work correctly. */}
          <Route
            element={
              <AdminProtectedRoute>
                <AdminLayout />
              </AdminProtectedRoute>
            }
          >
            <Route path="dashboard" element={<AdminDashboardHome />} />
            <Route path="links" element={<ManageLinks />} />
            <Route path="tenders" element={<ManageTenders />} />
            <Route path="student-registration" element={<ManageStudentRegistration />} />
            {/* Any other sidebar item (not built yet) falls through to a labeled
                placeholder instead of a blank page, so it's obvious what you're
                looking at. */}
              <Route path="download-forms" element={<ManageDownloadForms />} />  
              <Route path="results" element={<ManageResults />} /> 
              <Route path="admission" element={<ManageAdmission />} /> 
              <Route path="manage-admission" element={<ManageAdmissionCourse />} />
              <Route path="govt-approval" element={<ManageGovtApproval />} />
              <Route path="events" element={<ManageEvents />} />   {/* ← ADD THIS */}
              <Route path="contents/main-menu" element={<ManageMainMenu />} />
              <Route path="contents/main-menu/add" element={<AddMainMenu />} />
              <Route path="contents/main-menu/edit" element={<EditMainMenu />} />
              <Route path="contents/sub-menu" element={<ManageSubMenu />} />
              <Route path="contents/sub-menu/add" element={<ManageSubMenuForm  />} />
              <Route path="contents/sub-menu/edit" element={<ManageSubMenuForm  />} />
              <Route path="contents/sub-sub-menu" element={<ManageSubSubMenu />} />
              <Route path="contents/sub-sub-menu/add" element={<AddSubSubMenu />}/>
              <Route path="contents/sub-sub-menu/edit" element={<EditSubSubMenu />}/>
              <Route path="contents/other" element={<ManageContents />} />
            <Route path="*" element={<AdminPlaceholderRoute />} />

            {/* // Added by chandrakanta sir ----------start--- */}
            <Route path="notice-board" element={<ManageDownloadNotices />} />
            <Route path="advertisement" element={<ManageAdvertisement />} />
            <Route path="success" element={<ManageSuccess />} /> {/* ← NEW (type = 8) */}
            <Route path="course" element={<ManageCourse />} /> 
            <Route path="department" element={<ManageDepartment />} />
            <Route path="mci-information" element={<ManageMciInformation />} /> 
            <Route path="activity-report" element={<ManageActivityReport />} />
            <Route path="settings" element={<ManageSetting />} />  
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="seo" element={<ManageSeo />} />
            <Route path="banners" element={<ManageBanner />} />
            <Route path="faculty/department" element={<ManageDepartmentMaster />} /> 
            <Route path="faculty/designation" element={<ManageDesignation />} />
            <Route path="faculty/details" element={<ManageFaculty />} />
            <Route path="gallery/category" element={<ManageGalleryCategory />} />
            <Route path="gallery/photos" element={<ManagePhotoGallery />} />
            {/* // Added by chandrakanta sir ----------end---- */}
          </Route>
        </Route>

        {/* All other pages use the main layout */}
        <Route path="/*" element={
          <MainLayout>
            <Routes>
              <Route path="/" element={<Home />} />
              {/* <Route
          path="/page/:id"
          element={<PageContent />}
        /> */}
        {/* <Route path="/page/:id/:slug?" element={<PageContent />} /> */}
                <Route path="/:mainSlug/:subSlug?/:subSubSlug?" element={<PageContent />} />
                <Route path="/notice-board/:endpoint" element={<NoticeListPage />} />
                <Route path="/web-link" element={<WebLinkPage />} />
                {/* <Route path="/gallery" element={<GalleryPage />} />
                <Route path="/gallery/:catSlug" element={<GalleryCategoryPage />} /> */}
                <Route path="/gallery/:catSlug?" element={<GalleryPage />} />
                <Route path="/contact" element={<ContactPage />} /> {/* CHANGE 3 (cont.): added here */}
                <Route path="/site-map" element={<SiteMapPage />} />
                <Route path="/faculty/:mode?/:catSlug?" element={<FacultyPage />} />
                <Route path="/admissions" element={<AdmissionsPage />} />
                <Route path="/govt-approval" element={<GovtApprovalPage />} />
            </Routes>
          </MainLayout>
        }/>
      </Routes>
    </Router>
  );
}